import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, Mail, Phone, MapPin, Check, ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion } from "@/components/FaqAccordion";
import { InlineLeadForm } from "@/components/InlineLeadForm";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";

const TITLE = "Book a Scoping Call | Freelancer Tamal";
const DESC =
  "Book a free 20-minute SEO scoping call with Rahatul Ashiq Tamal. Bring your site and your goal; leave with an honest read on whether SEO is worth it for you.";

export const Route = createFileRoute("/book")({
  head: () =>
    buildSeo({
      title: TITLE,
      description: DESC,
      path: "/book",
      ogEyebrow: "Book a call",
      keywords:
        "book SEO consultation, free SEO call Bangladesh, SEO scoping call, hire SEO consultant Bangladesh, SEO discovery call",
      alternates: { bn: "/bn/book" },
    }),
  component: BookPage,
});

const AGENDA = [
  {
    t: "Minutes 0–5 — where you are",
    d: "Your site, your market, and what you've already tried. I'll have looked at your site before the call, so we skip the introductions.",
  },
  {
    t: "Minutes 5–12 — what I'd look at first",
    d: "The two or three things most likely holding you back. You get these whether or not we work together.",
  },
  {
    t: "Minutes 12–20 — is this worth doing",
    d: "Realistic scope, realistic timeline, realistic budget. If SEO isn't the right spend for you right now, I'll say so.",
  },
];

const PREP = [
  "Your website URL (and staging URL if you're mid-rebuild)",
  "The one business outcome you want more of — calls, orders, demos, applications",
  "Two or three competitors you'd like to outrank",
  "Read-only Search Console or GA4 access, if you have it handy",
  "Your rough monthly budget range — it changes what I recommend",
];

const FAQ = [
  {
    q: "Is the call really free?",
    a: "Yes. Twenty minutes, no charge, no obligation. I'd rather spend twenty minutes finding out we're not a fit than start an engagement that disappoints both of us.",
  },
  {
    q: "What time zones do you work across?",
    a: "I'm in Bangladesh (UTC+6) and regularly run calls with clients in the US, UK, Australia and the Middle East. Early mornings and late evenings Dhaka time are both available.",
  },
  {
    q: "What happens after the call?",
    a: "Within one business day you get a short written summary: what I'd prioritise, the tier I'd suggest, and a price. No slide deck, no chasing.",
  },
  {
    q: "Can we talk in Bangla?",
    a: "Of course. বাংলা বা ইংরেজি — যেটায় স্বচ্ছন্দ, সেটাতেই কথা বলি।",
  },
];

function BookPage() {
  const hasEmbed = Boolean(SITE.booking.url);
  const whatsapp = `https://wa.me/${SITE.phone.replace(/[^0-9]/g, "")}`;

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "ReserveAction",
      "@id": `${SITE.url}/book#reserve`,
      name: "Book a 20-minute SEO scoping call",
      target: SITE.booking.url || `${SITE.url}/book`,
      agent: {
        "@type": "Person",
        "@id": `${SITE.url}/#person`,
        name: SITE.fullName,
        url: `${SITE.url}/about`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": `${SITE.url}/book#page`,
      url: `${SITE.url}/book`,
      name: TITLE,
      description: DESC,
      inLanguage: "en",
    },
  ];

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Book a call", path: "/book" },
        ]}
      />
      <h1 className="sr-only">Book a scoping call with Freelancer Tamal</h1>
      <JsonLd data={schema} />

      <Section
        eyebrow="Book a call"
        title={
          <>
            Twenty minutes. <span className="text-gradient">An honest read.</span>
          </>
        }
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          No pitch deck and no discovery-call theatre. You tell me what you're
          trying to grow, I tell you whether search can do it and what it would
          take.
        </p>

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            {hasEmbed ? (
              <>
                <div className="rounded-3xl border border-border overflow-hidden bg-card/30">
                  <iframe
                    src={SITE.booking.url}
                    title="Booking calendar"
                    loading="lazy"
                    className="w-full h-[720px] border-0"
                  />
                </div>
                <div className="mt-6">
                  <InlineLeadForm
                    title="Or send the brief now"
                    description="Give me the site and the goal and I'll come to the call already knowing where the problems are."
                    source="book-page"
                  />
                </div>
              </>
            ) : (
              /* No calendar connected yet — the form is the primary path, so it
                 leads and the direct contact details back it up. */
              <>
                <InlineLeadForm
                  title="Request your slot"
                  description="Tell me the site and the goal. I reply within one business day with two times that suit you."
                  source="book-page"
                />
                <div className="mt-6 rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-8 lg:p-10">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
                    <Calendar size={14} /> Prefer to message?
                  </div>
                  <h2 className="mt-4 font-display text-3xl leading-tight">
                    Email or WhatsApp works just as well.
                  </h2>
                  <p className="mt-4 text-muted-foreground">
                    Either route reaches me directly — I reply within one
                    business day, usually much sooner.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href={`mailto:${SITE.email}?subject=Scoping%20call%20request`}
                      className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
                    >
                      <Mail size={16} /> {SITE.email}
                    </a>
                    <a
                      href={whatsapp}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-primary/50"
                    >
                      <Phone size={16} /> WhatsApp {SITE.phone}
                    </a>
                  </div>
                  <p className="mt-6 text-xs text-muted-foreground flex items-center gap-2">
                    <MapPin size={13} /> {SITE.city}, {SITE.country} — UTC+6
                  </p>
                </div>
              </>
            )}
          </div>


          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl border border-border p-8">
              <h2 className="font-display text-2xl">What happens on the call</h2>
              <ul className="mt-6 space-y-5">
                {AGENDA.map((a) => (
                  <li key={a.t}>
                    <div className="text-sm font-medium">{a.t}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{a.d}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-border p-8">
              <h2 className="font-display text-2xl">Bring these</h2>
              <ul className="mt-6 space-y-3">
                {PREP.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Check size={16} className="text-primary mt-0.5 flex-none" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-border p-8">
              <h2 className="font-display text-2xl">Know the budget first?</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Every tier and price is published — read it before we talk.
              </p>
              <Link
                to="/pricing"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-primary/50"
              >
                See pricing <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="FAQ" title="Before you book.">
        <FaqAccordion items={FAQ} idPrefix="faq-book" />
      </Section>
    </>
  );
}
